import dayjs from "dayjs";
import "dayjs/locale/es";
import { COUNTRIES } from "../reservas/components/ReservaShared";

/*
 * Pure helpers of the public birthday flow (club landing section, booking flow and pay-link page).
 * No React and no network: everything here is testable on its own. The shapes come from the back's
 * `toPublicBirthdays` (club.birthdays), the availability endpoint and `getPublicRequestView`.
 */

const WEEKDAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
// Display order Monday..Sunday; the data uses 0 = Sunday .. 6 = Saturday.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const QUESTION_KEYS = ["birthdayPerson", "age", "guests", "comments"];

export const formatMoney = (value) => `$${Number(value || 0).toLocaleString("es-AR")}`;

export const formatDuration = (minutes) => {
  const total = Number(minutes) || 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

/** "2026-11-14" -> "sábado 14 de noviembre". */
export const formatLongDate = (isoDate) => {
  if (!isoDate) return "";
  const d = dayjs(String(isoDate).slice(0, 10));
  return d.isValid() ? d.locale("es").format("dddd D [de] MMMM") : "";
};

/** Instant (ISO string) -> "sábado 14 de noviembre, 18:30 hs". */
export const formatDeadline = (iso) => {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? `${d.locale("es").format("dddd D [de] MMMM")}, ${d.format("HH:mm")} hs` : "";
};

/**
 * Groups the club's windows into short readable lines: "Lun a Vie · 14:00 a 20:00".
 * Consecutive weekdays collapse into a range; isolated ones are listed.
 */
export const summarizeWindows = (windows) => {
  if (!Array.isArray(windows)) return [];
  return windows
    .filter((w) => Array.isArray(w?.weekdays) && w.weekdays.length > 0)
    .map((w) => {
      const days = WEEK_ORDER.filter((d) => w.weekdays.includes(d));
      const runs = [];
      days.forEach((d) => {
        const last = runs[runs.length - 1];
        if (last && WEEK_ORDER.indexOf(d) === WEEK_ORDER.indexOf(last[last.length - 1]) + 1) last.push(d);
        else runs.push([d]);
      });
      const label = runs
        .map((run) =>
          run.length >= 3
            ? `${WEEKDAY_LABELS[run[0]]} a ${WEEKDAY_LABELS[run[run.length - 1]]}`
            : run.map((d) => WEEKDAY_LABELS[d]).join(", ")
        )
        .join(", ");
      return `${label} · ${w.start} a ${w.end}`;
    });
};

/** Deposit in whole pesos, same rounding as the back (`birthdayDepositAmount`). */
export const computeAmounts = (price, depositPercent) => {
  const total = Number(price) || 0;
  const deposit = Math.min(total, Math.round((total * (Number(depositPercent) || 0)) / 100));
  return { total, deposit, balance: Math.max(total - deposit, 0) };
};

/** A club with a deposit configured but without validated payments cannot take birthdays with online deposit. */
export const isDepositBlocked = (config) =>
  Boolean(config) && Number(config.depositPercent) > 0 && config.canChargeDeposit === false;

export const enabledQuestions = (questions) => {
  const q = questions || {};
  return QUESTION_KEYS.filter((key) => q[key] !== false);
};

/**
 * Bookable dates: from today + the minimum advance (in whole days) up to the maximum advance,
 * keeping only weekdays that fall inside some window. `now` is injectable for tests.
 */
export const birthdayDateOptions = (config, now = dayjs()) => {
  if (!config) return [];
  const allowedDays = new Set((config.windows || []).flatMap((w) => w.weekdays || []));
  const from = Math.max(0, Math.floor((Number(config.minAdvanceHours) || 0) / 24));
  const to = Math.max(from, Number(config.maxAdvanceDays) || 0);
  const options = [];
  for (let offset = from; offset <= to; offset += 1) {
    const d = now.startOf("day").add(offset, "day");
    if (allowedDays.has(d.day())) {
      options.push({
        date: d.format("YYYY-MM-DD"),
        weekday: WEEKDAY_LABELS[d.day()],
        day: d.format("D"),
        month: d.locale("es").format("MMM"),
      });
    }
  }
  return options;
};

/** "Cancha X" name of the court the back will assign (first free court of the chosen start). */
export const courtNameFor = (courtId, courts) => {
  const court = (courts || []).find((c) => String(c._id || c.courtId) === String(courtId));
  return court?.name || "";
};

/** Phone as the court checkout sends it: "<prefix> <number without the prefix>". */
export const formatPhone = (countryCode, number) => {
  const country = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
  const raw = String(number || "").trim();
  if (!raw) return "";
  const prefixDigits = country.prefix.replace(/\D/g, "");
  return `${country.prefix} ${raw.replace(new RegExp(`^(\\+?${prefixDigits}\\s*)`), "")}`;
};

const toInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
};

/** `details` with ONLY the questions the club enabled (the back drops the others anyway). */
export const buildDetails = (form, questions) => {
  const details = {};
  enabledQuestions(questions).forEach((key) => {
    if (key === "birthdayPerson" || key === "comments") {
      const text = String(form?.[key] || "").trim();
      if (text) details[key] = text;
    } else {
      const n = toInt(form?.[key]);
      if (n !== null) details[key] = n;
    }
  });
  return details;
};

/** Body shared by the direct reservation and the approval request. */
export const buildBirthdayPayload = ({ selection, form, questions, mode, origin }) => {
  const payload = {
    packageId: selection.packageId,
    date: selection.date,
    slotStart: selection.slotStart,
    name: String(form.name || "").trim(),
    lastname: "",
    email: String(form.email || "").trim(),
    phone: formatPhone(form.countryCode, form.phone),
    dni: String(form.dni || "").trim(),
    details: buildDetails(form, questions),
    policyAccepted: true,
  };
  if (selection.courtId) payload.courtId = selection.courtId;
  // Fiserv returns to the page the buyer started from; only a direct booking goes to the gateway.
  if (mode === "direct" && origin) payload.checkoutOrigin = origin;
  return payload;
};

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/** Field errors of the details step; an empty object means the form can be sent. */
export const validateDetailsForm = (form, questions, policyAccepted) => {
  const errors = {};
  if (!String(form.name || "").trim()) errors.name = "Ingresá tu nombre.";
  if (!EMAIL_PATTERN.test(String(form.email || "").trim())) errors.email = "Ingresá un email válido.";
  const country = COUNTRIES.find((c) => c.code === form.countryCode) || COUNTRIES[0];
  if (!String(form.phone || "").trim()) errors.phone = "Ingresá tu teléfono.";
  else if (!country.validate(String(form.phone))) errors.phone = "Este número no es válido.";
  if (!/^\d{7,10}$/.test(String(form.dni || "").trim())) errors.dni = "DNI inválido (debe tener entre 7 y 10 números).";

  const enabled = enabledQuestions(questions);
  if (enabled.includes("age") && form.age !== "" && !(toInt(form.age) >= 1)) errors.age = "Ingresá una edad válida.";
  if (enabled.includes("guests") && form.guests !== "" && !(toInt(form.guests) >= 1)) {
    errors.guests = "Ingresá una cantidad de invitados válida.";
  }
  if (!policyAccepted) errors.policy = "Tenés que aceptar la política para continuar.";
  return errors;
};

export const EMPTY_FORM = {
  birthdayPerson: "",
  age: "",
  guests: "",
  comments: "",
  name: "",
  email: "",
  countryCode: "AR",
  phone: "",
  dni: "",
};

// The back's messages when the chosen start was taken or blocked meanwhile (409 or 400).
const SLOT_GONE_PATTERN = /superpone|bloqueado|ocupad|no está disponible/i;

/**
 * Where an API error should land: "slot" (back to the date step with a refreshed availability) or
 * "form" (inline on the details step). Always carries a message the buyer can read.
 */
export const classifyBookingError = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if ((status === 409 || status === 400) && SLOT_GONE_PATTERN.test(message || "")) {
    return { kind: "slot", message: message || "Ese horario ya no está disponible. Elegí otro." };
  }
  if (status === 429) {
    return {
      kind: "form",
      message: message || "Ya tenés varias solicitudes pendientes en este club. Esperá su respuesta antes de enviar otra.",
    };
  }
  return { kind: "form", message: message || "No pudimos enviar tu cumple. Probá de nuevo en un momento." };
};

const CONFIRMED_RESERVATION = ["confirmed", "reserved"];

/**
 * Which screen the pay-link page shows for the public view. Order matters: a confirmed reservation wins over
 * the request status. Unknown statuses (e.g. a later "proposed") fall to "other" instead of breaking.
 * @returns {"pending"|"payable"|"confirmed"|"rejected"|"expired"|"cancelled"|"other"}
 */
export const resolveRequestState = (view) => {
  if (!view) return "other";
  if (CONFIRMED_RESERVATION.includes(view.reservation?.status)) return "confirmed";
  switch (view.status) {
    case "pending":
      return "pending";
    case "rejected":
      return "rejected";
    case "expired":
      return "expired";
    case "cancelled":
      return "cancelled";
    case "accepted":
      // Accepted but not payable any more: the payment deadline passed or the hold was released.
      return view.payable ? "payable" : "expired";
    default:
      return "other";
  }
};

// The public request view carries no club id, so the browser that sent the request remembers it: the pay-link
// page uses it to offer "ask for another date" inside the right club. Another device falls back to the club list.
const REQUEST_CLUB_KEY = (token) => `birthday:request-club:${token}`;

export const rememberRequestClub = (token, clubId) => {
  if (!token || !clubId) return;
  try {
    localStorage.setItem(REQUEST_CLUB_KEY(token), String(clubId));
  } catch (_) {
    // Storage can be unavailable (private mode): the page just falls back to the club list.
  }
};

export const recallRequestClub = (token) => {
  try {
    return localStorage.getItem(REQUEST_CLUB_KEY(token)) || null;
  } catch (_) {
    return null;
  }
};

/** Club services for the landing: the "Cumpleaños" chip is redundant when the club has the birthday section. */
export const visibleServices = (club) => {
  const services = Array.isArray(club?.services) ? club.services : [];
  return club?.birthdays ? services.filter((s) => s !== "cumpleanos") : services;
};
